import { useEffect, useRef, useState } from 'react';
import Icon from './Icon';
import CalendarPopover from './CalendarPopover';
import { parseDateInput, humanDay } from '../when';

/**
 * A DATE BOX A PERSON CAN TYPE INTO — and keep what they typed. The one date
 * control across the whole app, on the card and in every form.
 *
 * The native `<input type="date">` this replaces reported its value as '' for
 * every half-formed date, so a hand-typed day was saved as nothing and silently
 * reverted (Vinnie, 20 Sep: "when you enter the date manually the card doesn't
 * keep it"). Only the calendar, which set a whole valid date at once, ever stuck.
 *
 * So this is a plain text box read by `parseDateInput`, which understands the
 * ways people actually write a date ("15 Sep", "15/09/2026", "next friday",
 * "+2w", an ISO day). As you type it shows the day it resolved to, so a date it
 * could not read is VISIBLE, never a silent guess or a silent loss — an
 * unreadable entry is flagged rather than saved blank. The calendar button opens
 * FlightDeck's own quiet month popover for anyone who would rather point at a day.
 *
 * `variant` picks the look: 'card' is the quiet, fixed-width field in the issue
 * card's property list; 'form' is a full-width bordered control for wizards,
 * sprints, the budget ledger and the like.
 */
export default function DateField({
  value,
  onChange,
  ariaLabel,
  ariaRequired,
  id,
  placeholder = 'e.g. 15 Sept 2026',
  disabled = false,
  min,
  max,
  variant = 'card',
  autoFocus = false,
  onCommit,
  onCancel,
  onInvalidChange,
}: {
  value: string;                       // the day as YYYY-MM-DD, or '' for none
  onChange: (iso: string) => void;     // a real day, or '' when cleared
  ariaLabel: string;
  ariaRequired?: boolean;              // mirror the visible required asterisk (R10)
  id?: string;
  placeholder?: string;
  disabled?: boolean;
  min?: string;                        // earliest allowed day (YYYY-MM-DD)
  max?: string;                        // latest allowed day (YYYY-MM-DD)
  variant?: 'card' | 'form';
  autoFocus?: boolean;                 // focus on mount (inline editors)
  onCommit?: (iso: string) => void;    // blur/Enter settled on this day ('' = cleared)
  onCancel?: () => void;               // Escape — discard, for an inline editor
  onInvalidChange?: (invalid: boolean) => void;  // the box holds an entry it can't accept
}) {
  const [text, setText] = useState(value ? humanDay(value) : '');
  const [focused, setFocused] = useState(false);
  const [preview, setPreview] = useState<string | null>(value || null);
  const [err, setErr] = useState<string | null>(null);
  const [open, setOpen] = useState(false);   // the calendar popover
  const btn = useRef<HTMLButtonElement>(null);
  const seen = useRef(value);       // the last value this field already reflects

  // Set (or lift) the flag AND tell the form, so it can hold Save while the box
  // holds an entry it could not accept. Reported synchronously from the same
  // event that set it, so a blur-then-Save click sees the fresh state.
  function flag(msg: string | null) { setErr(msg); onInvalidChange?.(!!msg); }

  // If a flagged field is unmounted (its row removed, the dialog closed), it
  // must not leave the form thinking it is still invalid.
  const invalidCb = useRef(onInvalidChange);
  invalidCb.current = onInvalidChange;
  useEffect(() => () => invalidCb.current?.(false), []);

  // Follow the outside value only when it changes from OUTSIDE — a card switch, a
  // date arriving late, a clear from elsewhere. A change this field made itself
  // (typing commits live) is already recorded, so this skips it and never fights
  // the box mid-edit or reformats a draft out from under a blur.
  useEffect(() => {
    if (value === seen.current) return;
    seen.current = value;
    if (focused) return;
    setText(value ? humanDay(value) : '');
    setPreview(value || null);
    flag(null);
  }, [value, focused]);

  /** A parsed day outside the allowed range — the reason, or null when fine. */
  function breaks(day: string): string | null {
    if (min && day < min) return `On or after ${humanDay(min)}`;
    if (max && day > max) return `On or before ${humanDay(max)}`;
    return null;
  }

  function type(next: string) {
    setText(next);
    flag(null);                     // never nag mid-type; judged on the way out
    const raw = next.trim();
    if (!raw) { setPreview(null); onChange(''); return; }
    const day = parseDateInput(raw);
    setPreview(day);
    // Land a readable, in-range date in state AS IT IS TYPED, so ⌘+Enter saves it
    // even when focus never left the box. Anything unreadable or out of range
    // changes nothing yet — it is caught on the way out (commit).
    if (day && !breaks(day)) onChange(day);
  }

  // Read the box and commit — an empty box clears the date, a readable in-range
  // one lands the day it resolved to, and an unreadable or out-of-range one is
  // held and flagged so nothing is lost. Returns the day that settled ('' when
  // cleared), or null when nothing could be committed — for onCommit.
  function commit(): string | null {
    const raw = text.trim();
    if (!raw) { flag(null); setPreview(null); onChange(''); return ''; }
    const day = parseDateInput(raw);
    if (!day) { flag('Not a date — try 15 Sept 2026, 2026-09-15, or +2w'); setPreview(null); return null; }
    const why = breaks(day);
    setPreview(day); setText(humanDay(day));
    if (why) { flag(why); return null; }     // reformat, but flag and do not commit
    flag(null); onChange(day); return day;
  }

  // Chosen from the calendar — a whole day in one go, like the picker always did.
  function pick(day: string) {
    flag(null); setPreview(day); setText(humanDay(day)); onChange(day); setOpen(false);
  }
  function clearFromCal() {
    flag(null); setPreview(null); setText(''); onChange(''); setOpen(false);
  }

  // While the box is being typed in and it reads as something other than the
  // finished date (e.g. "15 sep", "15/9"), show the day it resolved to — so an
  // ambiguous or shorthand date is confirmed before the box reformats on blur.
  const showResolved = focused && preview && text.trim() !== humanDay(preview);
  const inputCls = variant === 'card' ? 'ctl quiet mdl-date' : 'ctl datefield-form-input';

  return (
    <span className={`datefield${variant === 'form' ? ' datefield--form' : ''}`}>
      <span className="datefield-row">
        <input
          type="text"
          id={id}
          className={`${inputCls}${err ? ' is-bad' : ''}`}
          aria-label={ariaLabel}
          aria-required={ariaRequired || undefined}
          aria-invalid={err ? true : undefined}
          disabled={disabled}
          autoComplete="off"
          spellCheck={false}
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus={autoFocus}
          placeholder={placeholder}
          value={text}
          onChange={(e) => type(e.target.value)}
          onFocus={(e) => { setFocused(true); e.target.select(); }}
          onBlur={() => { setFocused(false); const r = commit(); if (onCommit && r !== null) onCommit(r); }}
          onKeyDown={(e) => {
            // Enter commits the box in place (blur then settles it); ⌘/Ctrl+Enter
            // is a form's own save, so let it through. Escape cancels an inline
            // editor when one asked for it, otherwise it is a dialog's own cancel.
            if (e.key === 'Enter' && !e.metaKey && !e.ctrlKey) { e.preventDefault(); (e.target as HTMLInputElement).blur(); }
            else if (e.key === 'Escape' && onCancel) { e.stopPropagation(); onCancel(); }
          }}
        />
        <button
          ref={btn}
          type="button"
          className={`datefield-pick${open ? ' is-open' : ''}`}
          aria-label={`${ariaLabel} — pick from a calendar`}
          aria-expanded={open}
          disabled={disabled}
          onClick={() => setOpen((o) => !o)}
        >
          <Icon name="calendar" size={16} />
        </button>
        {open && (
          <CalendarPopover
            value={value}
            min={min}
            max={max}
            onPick={pick}
            onClear={clearFromCal}
            onClose={() => setOpen(false)}
            anchorRef={btn}
          />
        )}
      </span>
      {err ? (
        <span className="datefield-note is-bad" role="alert">{err}</span>
      ) : showResolved ? (
        <span className="datefield-note" aria-live="polite">{humanDay(preview!)}</span>
      ) : null}
    </span>
  );
}
